import { USER_ROLE } from '@/constants/user';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { userRoleSelector } from '@/redux/user/user.selectors';

/**
 * Hook for admin permissions based on user roles

 * Example usage:
 * ```typescript
 * const permissions = useAdminPermissions();
 *
 * // Simple role check - backend handles all project access filtering
 * if (permissions.canUpdateProject) {
 *   showEditButton(); // Button only shows for resources the user can access
 * }
 * ```
 */
export const useAdminPermissions = (): {
  // User management
  canViewUsers: boolean;
  canCreateUser: boolean;
  canUpdateUser: boolean;
  canRemoveUser: boolean;
  canManageUserPrivileges: boolean;

  // Project management
  canViewProjects: boolean;
  canCreateProject: boolean;
  canUpdateProject: boolean;
  canRemoveProject: boolean;
  canViewProjectLogs: boolean;
  canManageProjectAccess: boolean;
  canViewProjectComments: boolean;
  canRemoveComments: boolean;
  canViewProjectMaps: boolean;
  canEditProjectMaps: boolean;
  canViewProjectOverlays: boolean;
  canEditProjectOverlays: boolean;
  canRemoveOverlays: boolean;
  canDownloadOverlaySource: boolean;

  // Plugin management
  canViewPlugins: boolean;
  canManagePlugins: boolean;

  // Annotation management
  canViewAnnotation: boolean;
  canManageAnnotation: boolean;

  // Permalink management
  canViewPermalinks: boolean;
  canManagePermalinks: boolean;

  // Configuration
  canViewConfig: boolean;
  canUpdateConfig: boolean;
  canRegisterUnregisterMachine: boolean;

  // Role checks
  isAdmin: boolean;
  isCurator: boolean;
  userRole: string | null;
} => {
  const userRole = useAppSelector(userRoleSelector);

  const isAdmin = userRole === USER_ROLE.ADMIN;
  const isCurator = userRole === USER_ROLE.CURATOR;

  return {
    // User management
    canViewUsers: isAdmin || isCurator,
    canCreateUser: isAdmin,
    canUpdateUser: isAdmin,
    canRemoveUser: isAdmin,
    canManageUserPrivileges: isAdmin, // Admin only: grantPrivilege/revokePrivilege endpoints

    // Project management
    canViewProjects: isAdmin || isCurator, // API filters projects based on READ_PROJECT permissions
    canCreateProject: isAdmin || isCurator,
    canUpdateProject: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canRemoveProject: isAdmin, // TODO: Add support for project owner removal
    canViewProjectLogs: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canManageProjectAccess: isAdmin,
    canViewProjectComments: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canRemoveComments: isAdmin || isCurator,
    canViewProjectMaps: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canEditProjectMaps: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canViewProjectOverlays: isAdmin || isCurator,
    canEditProjectOverlays: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canRemoveOverlays: isAdmin || isCurator, // Assumes isCurator has READ_PROJECT
    canDownloadOverlaySource: isAdmin || isCurator,

    // Plugin management
    canViewPlugins: isAdmin || isCurator,
    canManagePlugins: isAdmin, // Admin only: add, update, delete plugins

    // Annotation management
    canViewAnnotation: isAdmin || isCurator,
    canManageAnnotation: isAdmin || isCurator,

    // Permalink management
    canViewPermalinks: isAdmin,
    canManagePermalinks: isAdmin,

    // Configuration
    canViewConfig: isAdmin,
    canUpdateConfig: isAdmin,
    canRegisterUnregisterMachine: isAdmin, // Admin only: registerMachine/unregisterMachine

    // Role checks
    isAdmin,
    isCurator,
    userRole,
  };
};
